import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Smartphone, Mail, Loader2, KeyRound } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/* Multi-colour Google "G" mark used by the fallback button — matches Google's brand guidelines. */
function GoogleGIcon({ className }) {
  return (
    <svg viewBox="0 0 18 18" className={className} aria-hidden focusable="false">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71a5.41 5.41 0 0 1-.282-1.71c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042L3.964 10.71z"/>
      <path fill="#EA4335" d="M9 3.579c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.579 9 3.579z"/>
    </svg>
  );
}

/**
 * Small, self-contained bundle of Phase-1 auth extras:
 *   • "Continue with Google" (Google Identity Services — GSI)
 *   • "Continue with Mobile Number" (Twilio Verify — inline dialog)
 *   • "Forgot password?" link (opens inline dialog)
 *
 * Renders nothing when no additional providers are enabled + reset also off.
 *
 * Zero-CLS strategy:
 *   1. On mount, hydrate `config` synchronously from localStorage so returning
 *      visitors see the final layout immediately (no shift).
 *   2. First-time visitors see fixed-size skeleton placeholders that occupy the
 *      exact same slots as the final controls; only opacity transitions.
 *   3. The Google button slot is always 44px tall + 320px wide; a skeleton
 *      overlays it until GSI finishes injecting the iframe.
 */
const AUTH_CONFIG_CACHE_KEY = "es_auth_config_v1";

function readCachedConfig() {
  try {
    const raw = localStorage.getItem(AUTH_CONFIG_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Basic sanity check
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}

export default function AuthExtras({ onForgotFallback }) {
  const nav = useNavigate();
  const { setSession } = useAuth();
  // Hydrate from cache synchronously; only null when we've never fetched.
  const [config, setConfig] = useState(() => readCachedConfig());
  // "signingIn" = user clicked the button; awaiting SDK ready + Google flow.
  const [signingIn, setSigningIn] = useState(false);
  // Track SDK readiness so click behaviour matches — no re-render of the button itself.
  const sdkReadyRef = useRef(false);
  const pendingClickRef = useRef(false);
  const initializedRef = useRef(false);

  const [phoneOpen, setPhoneOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/config");
        setConfig(data || {});
        try { localStorage.setItem(AUTH_CONFIG_CACHE_KEY, JSON.stringify(data || {})); } catch { /* ignore */ }
      } catch { /* ignore — keep cached value */ }
    })();
  }, []);

  // Prefer whatever client_id we know about (cache or fresh).
  const clientId = config?.google_client_id || "";

  // Initialize GSI when SDK + client_id are both available.
  const initializeGSI = useCallback(() => {
    if (initializedRef.current || !window.google?.accounts?.id || !clientId) return;
    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (resp) => {
          try {
            const { data } = await api.post("/auth/google", { credential: resp.credential });
            setSession(data.token, data.user);
            toast.success("Signed in with Google");
            nav("/dashboard", { replace: true });
          } catch (err) {
            toast.error(formatApiError(err, "Google sign-in failed"));
          } finally {
            setSigningIn(false);
          }
        },
        ux_mode: "popup",
      });
      initializedRef.current = true;
      sdkReadyRef.current = true;
      // If the user clicked before init finished, honour the pending click now.
      if (pendingClickRef.current) {
        pendingClickRef.current = false;
        try { window.google.accounts.id.prompt(); } catch { /* ignore */ }
      }
    } catch { /* init failed — clicking will show a toast */ }
  }, [clientId, nav, setSession]);

  // Load the GSI script eagerly on mount (parallel to first paint).
  useEffect(() => {
    if (window.google?.accounts?.id) { initializeGSI(); return; }
    const src = "https://accounts.google.com/gsi/client";
    let s = document.querySelector(`script[src="${src}"]`);
    const onLoad = () => initializeGSI();
    if (!s) {
      s = document.createElement("script");
      s.src = src; s.async = true; s.defer = true;
      s.onload = onLoad;
      document.body.appendChild(s);
    } else {
      s.addEventListener("load", onLoad);
    }
    return () => { s?.removeEventListener?.("load", onLoad); };
  }, [initializeGSI]);

  // Also re-run init when clientId becomes known after script has already loaded.
  useEffect(() => { initializeGSI(); }, [initializeGSI]);

  // Click handler — native button drives Google OAuth programmatically.
  const onGoogleClick = () => {
    if (signingIn) return;
    setSigningIn(true);
    if (window.google?.accounts?.id && sdkReadyRef.current) {
      try {
        window.google.accounts.id.prompt();
        return;
      } catch {
        setSigningIn(false);
        toast.error("Google sign-in is temporarily unavailable — please use email");
        return;
      }
    }
    // SDK not ready yet — remember the click; initializeGSI() will honour it when the script arrives.
    pendingClickRef.current = true;
    // Safety net so we never spin forever.
    setTimeout(() => {
      if (pendingClickRef.current) {
        pendingClickRef.current = false;
        setSigningIn(false);
        toast.error("Google sign-in is temporarily unavailable — please use email");
      }
    }, 8000);
  };

  // ---- Slot visibility ----
  // Google slot + divider are ALWAYS visible from first paint. No skeleton,
  // no waiting on SDK, no waiting on /auth/config.
  const configResolved = config !== null;
  const phoneActive = configResolved && !!config?.phone_enabled;

  return (
    <div className="mt-4">
      {/* Divider — always visible on first paint */}
      <div className="my-3 flex h-4 items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        <div className="h-px flex-1 bg-border" />
        Or continue with
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="flex flex-col items-stretch gap-2.5">
        {/* Google button — native, visible on first paint. GSI SDK loads in background;
            clicking triggers the OAuth popup either immediately (if ready) or after SDK arrives. */}
        <div className="relative mx-auto flex h-11 w-full max-w-[320px] items-center justify-center" data-testid="google-btn-slot">
          <button
            type="button"
            onClick={onGoogleClick}
            disabled={signingIn}
            aria-busy={signingIn}
            className="group absolute inset-0 flex cursor-pointer items-center justify-center gap-2 rounded-full border border-border bg-background text-[14px] font-semibold text-foreground shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition-[transform,box-shadow,background-color,border-color] duration-200 ease-out will-change-transform hover:-translate-y-px hover:border-neutral-300 hover:bg-[#FAFAFA] hover:shadow-[0_8px_18px_-10px_rgba(15,23,42,0.22),0_2px_6px_-2px_rgba(15,23,42,0.10)] active:scale-[0.98] active:shadow-[0_1px_2px_rgba(15,23,42,0.05)] active:duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-wait disabled:opacity-90 disabled:hover:translate-y-0 disabled:hover:shadow-[0_1px_2px_rgba(15,23,42,0.06)] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100 dark:hover:bg-neutral-900/60"
            data-testid="google-signin-button"
          >
            {signingIn ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Connecting…
              </>
            ) : (
              <>
                <GoogleGIcon className="h-4 w-4" />
                Continue with Google
              </>
            )}
          </button>
        </div>

        {phoneActive && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setPhoneOpen(true)}
            className="h-11 rounded-full text-[14px] font-semibold"
            data-testid="continue-with-phone-button"
          >
            <Smartphone className="h-4 w-4" /> Continue with Mobile Number
          </Button>
        )}
      </div>

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={() => (config?.email_reset_enabled ? setForgotOpen(true) : (onForgotFallback ? onForgotFallback() : setForgotOpen(true)))}
          className="text-[12.5px] font-semibold text-primary hover:underline"
          data-testid="forgot-password-link"
        >
          Forgot password?
        </button>
      </div>

      {phoneOpen && (
        <PhoneOtpDialog open={phoneOpen} onOpenChange={setPhoneOpen} />
      )}
      {forgotOpen && (
        <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} enabled={!!config?.email_reset_enabled} />
      )}
    </div>
  );
}

/* ---------------- Phone OTP dialog ---------------- */
function PhoneOtpDialog({ open, onOpenChange }) {
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [step, setStep] = useState("phone"); // phone | code
  const [phone, setPhone] = useState("");
  const [normalized, setNormalized] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async () => {
    if (!phone.trim()) return toast.error("Enter your mobile number");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/send-otp", { phone: phone.trim() });
      setNormalized(data.phone);
      setStep("code");
      setCooldown(30);
      toast.success("OTP sent");
    } catch (err) { toast.error(formatApiError(err, "Failed to send OTP")); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (!/^\d{4,6}$/.test(code)) return toast.error("Enter the code");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/verify-otp", { phone: normalized || phone.trim(), code });
      setSession(data.token, data.user);
      toast.success("Signed in");
      onOpenChange(false);
      nav("/", { replace: true });
    } catch (err) { toast.error(formatApiError(err, "Verification failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {step === "phone" ? "Continue with mobile" : "Enter the code"}
          </DialogTitle>
        </DialogHeader>
        {step === "phone" ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="phone-in">Mobile number</Label>
              <Input
                id="phone-in"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+91 98765 43210"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoFocus
                data-testid="phone-input"
              />
              <div className="text-xs text-muted-foreground">Indian numbers work without country code — we&apos;ll add +91 automatically.</div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-[13px] text-muted-foreground">Sent to <span className="font-semibold text-foreground">{normalized || phone}</span></div>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="text-center text-2xl font-display tracking-[0.4em]"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="••••"
              autoFocus
              data-testid="otp-input"
            />
            <button
              type="button"
              disabled={cooldown > 0 || busy}
              onClick={send}
              className="text-[12.5px] font-semibold text-primary disabled:text-muted-foreground disabled:no-underline hover:underline"
              data-testid="otp-resend"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>
        )}
        <DialogFooter className="gap-2">
          {step === "code" && (
            <Button variant="ghost" onClick={() => setStep("phone")} disabled={busy}>Back</Button>
          )}
          <Button
            onClick={step === "phone" ? send : verify}
            disabled={busy}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid={step === "phone" ? "phone-send-otp" : "phone-verify-otp"}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (step === "phone" ? "Send OTP" : "Verify & Continue")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Forgot password dialog ---------------- */
function ForgotPasswordDialog({ open, onOpenChange, enabled }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const send = async () => {
    if (!email.trim()) return toast.error("Enter your email");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setDone(true);
    } catch (err) { toast.error(formatApiError(err, "Something went wrong")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl inline-flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Reset password
          </DialogTitle>
        </DialogHeader>
        {done ? (
          <div className="space-y-2 text-sm">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
              If an account exists for <span className="font-semibold">{email}</span>, a password reset link has been sent.
            </div>
            {!enabled && (
              <div className="rounded-lg border border-amber-300/50 bg-amber-50/50 p-3 text-[12.5px] text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
                Note: email delivery is not yet configured on this server. Once the admin adds the email provider credentials, the reset email will be delivered.
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="fp-email"><Mail className="mr-1 inline h-3.5 w-3.5" /> Email address</Label>
              <Input
                id="fp-email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                data-testid="forgot-email-input"
              />
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          {!done && (
            <Button
              onClick={send}
              disabled={busy}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="forgot-send-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Reset Link"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Silence unused import warnings from bundler
void API;
